// src/components/AlertWidget.js
import React, { useState, useEffect } from 'react';
import { 
  Bell, Send, Mail, MessageCircle, Phone, AlertTriangle, 
  X, CheckCircle, Clock, Users, Eye, EyeOff, 
  Zap, History, Warehouse
} from 'lucide-react';
import { useNotifications, notificationManager } from '../manager_notifications';
import { getPriorityColorClass } from '../manager_notifications';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const AlertWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [channelsStatus, setChannelsStatus] = useState(null);
  
  // Utiliser le NotificationManager pour l'historique
  const { notifications: alertHistory, refresh } = useNotifications({
    filters: { sources: ['alert'] },
    refreshInterval: 30000
  });
  
  // Form state
  const [alertType, setAlertType] = useState('warning');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [channels, setChannels] = useState({
    email: true,
    telegram: false,
    sms: false
  });
  const [customRecipients, setCustomRecipients] = useState('');
  const [smsRecipients, setSmsRecipients] = useState('');
  
  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [lastAlert, setLastAlert] = useState(null);

  // Load channels status on mount
  useEffect(() => {
    fetchChannelsStatus();
    
    const interval = setInterval(fetchChannelsStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchChannelsStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/alerts/channels/status`);
      const data = await response.json();
      setChannelsStatus(data);
    } catch (err) {
      console.error('Failed to fetch channels status:', err);
    }
  };

  const getPriorityColor = (priority) => {
    const priorityMap = {
      emergency: 'critical',
      warning: 'high',
      info: 'low',
      success: 'normal'
    };
    return getPriorityColorClass(priorityMap[priority] || 'normal');
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'emergency': return <AlertTriangle size={16} className="text-red-400" />;
      case 'warning': return <AlertTriangle size={16} className="text-yellow-400" />;
      case 'info': return <Bell size={16} className="text-blue-400" />;
      case 'success': return <CheckCircle size={16} className="text-green-400" />;
      default: return <Bell size={16} className="text-gray-400" />;
    }
  };

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'email': return <Mail size={14} className="text-blue-400" />;
      case 'telegram': return <MessageCircle size={14} className="text-blue-400" />;
      case 'sms': return <Phone size={14} className="text-green-400" />;
      default: return <Bell size={14} className="text-gray-400" />;
    }
  };

  const handleSendAlert = async () => {
    if (!title.trim()) {
      alert('Please enter an alert title');
      return;
    }
    
    if (!message.trim()) {
      alert('Please enter an alert message');
      return;
    }
    
    const selectedChannels = Object.entries(channels).filter(([_, enabled]) => enabled).map(([key]) => key);
    if (selectedChannels.length === 0) {
      alert('Please select at least one notification channel');
      return;
    }
    
    setSending(true);
    
    try {
      const payload = {
        title: title,
        message: message,
        priority: alertType,
        channels: selectedChannels
      };
      
      if (targetAudience === 'all') {
        payload.send_to_all_owners = true;
      } else if (targetAudience === 'hangar') {
        payload.send_to_hangar_tenants = true;
      } else if (targetAudience === 'custom' && customRecipients) {
        const emails = customRecipients.split(',').map(e => e.trim()).filter(e => e);
        payload.email_recipients = emails;
        
        if (channels.sms && smsRecipients) {
          const phones = smsRecipients.split(',').map(p => p.trim()).filter(p => p);
          payload.sms_recipients = phones.map(phone => ({ phone, name: 'Customer' }));
        }
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/alerts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      setLastAlert({
        title,
        message,
        priority: alertType,
        channels: selectedChannels,
        success: response.ok,
        sent_at: new Date().toISOString()
      });
      
      setTitle('');
      setMessage('');
      setCustomRecipients('');
      setSmsRecipients('');
      
      // Rafraîchir l'historique via le manager
      await refresh();
      
      setTimeout(() => setLastAlert(null), 5000);
      
    } catch (err) {
      console.error('Failed to send alert:', err);
      alert('Failed to send alert: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const getAlertTypeLabel = (type) => {
    switch(type) {
      case 'emergency': return '🚨 EMERGENCY';
      case 'warning': return '⚠️ WARNING';
      case 'info': return 'ℹ️ INFO';
      case 'success': return '✅ SUCCESS';
      default: return '📢 ALERT';
    }
  };

  // Convertir les notifications du manager en format attendu
  const historyList = alertHistory.map(notif => ({
    id: notif.sourceId,
    title: notif.title,
    message: notif.message,
    priority: notif.type,
    sent_at: notif.timestamp,
    channels_used: notif.metadata?.channelsUsed || [],
    recipients_count: notif.metadata?.recipientsCount || 0,
    success: notif.metadata?.success !== false
  }));

  return (
    <>
      {/* Floating Alert Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-red-600 hover:bg-red-700 text-white rounded-full p-4 shadow-lg transition-all duration-200 flex items-center gap-2 group"
      >
        <Bell size={24} />
        <span className="hidden group-hover:inline text-sm font-medium mr-2">Send Alert</span>
        {alertHistory.filter(a => a.priority === 'critical').length > 0 && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-pulse" />
        )}
      </button>

      {/* Alert Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            
            {/* Header */}
            <div className="p-5 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Bell className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Alert Center</h3>
                  <p className="text-gray-400 text-xs">Send instant notifications to clients and staff</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition"
                  title="View history"
                >
                  <History size={18} className="text-gray-400" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Channels Status Bar */}
            <div className="px-5 py-3 bg-gray-700/30 border-b border-gray-700 flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <span className="text-gray-400">Channels:</span>
                <div className="flex items-center gap-2">
                  <Mail size={12} className="text-blue-400" />
                  <span className={channelsStatus?.email?.configured ? "text-green-400" : "text-red-400"}>
                    {channelsStatus?.email?.configured ? "Email Ready" : "No SMTP"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle size={12} className="text-blue-400" />
                  <span className={channelsStatus?.telegram?.configured ? "text-green-400" : "text-yellow-400"}>
                    {channelsStatus?.telegram?.configured ? "Telegram Ready" : "Not Configured"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={12} className="text-green-400" />
                  <span className={channelsStatus?.sms?.configured ? "text-green-400" : "text-yellow-400"}>
                    {channelsStatus?.sms?.configured ? "SMS Ready" : "Not Configured"}
                  </span>
                </div>
              </div>
              <button
                onClick={fetchChannelsStatus}
                className="text-gray-500 hover:text-white"
              >
                <Zap size={12} />
              </button>
            </div>

            {/* Success/Error Message */}
            {lastAlert && (
              <div className={`mx-5 mt-4 p-3 rounded-lg ${lastAlert.success ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                <div className="flex items-center gap-2">
                  {lastAlert.success ? <CheckCircle size={16} className="text-green-400" /> : <AlertTriangle size={16} className="text-red-400" />}
                  <span className="text-sm text-white">
                    {lastAlert.success ? 'Alert sent successfully!' : 'Failed to send alert'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {lastAlert.channels?.join(', ')} • {new Date(lastAlert.sent_at).toLocaleTimeString()}
                </p>
              </div>
            )}

            {/* Main Content */}
            {!showHistory ? (
              <div className="p-5 space-y-5">
                {/* Alert Type Selection */}
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Alert Priority</label>
                  <div className="flex gap-2">
                    {['info', 'warning', 'emergency', 'success'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setAlertType(type)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                          alertType === type
                            ? getPriorityColor(type) + ' text-white'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                      >
                        {type === 'emergency' && '🚨'}
                        {type === 'warning' && '⚠️'}
                        {type === 'info' && 'ℹ️'}
                        {type === 'success' && '✅'}
                        {' '}{type.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Alert Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Airport Closure, Weather Alert, Emergency..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    maxLength="100"
                  />
                  <p className="text-gray-500 text-xs mt-1">{title.length}/100 characters</p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Alert Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the situation, provide instructions, etc."
                    rows={4}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    maxLength="2000"
                  />
                  <p className="text-gray-500 text-xs mt-1">{message.length}/2000 characters</p>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Target Audience</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setTargetAudience('all')}
                      className={`py-2 rounded-lg text-sm transition ${
                        targetAudience === 'all'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      <Users size={14} className="inline mr-1" />
                      All Owners
                    </button>
                    <button
                      onClick={() => setTargetAudience('hangar')}
                      className={`py-2 rounded-lg text-sm transition ${
                        targetAudience === 'hangar'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      <Warehouse size={14} className="inline mr-1" />
                      Hangar Tenants
                    </button>
                    <button
                      onClick={() => setTargetAudience('custom')}
                      className={`py-2 rounded-lg text-sm transition ${
                        targetAudience === 'custom'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      <Users size={14} className="inline mr-1" />
                      Custom
                    </button>
                  </div>
                </div>

                {/* Custom Recipients */}
                {targetAudience === 'custom' && (
                  <div className="space-y-3 pl-2 border-l-2 border-blue-500">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Email Recipients</label>
                      <input
                        type="text"
                        value={customRecipients}
                        onChange={(e) => setCustomRecipients(e.target.value)}
                        placeholder="email1@example.com, email2@example.com"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm"
                      />
                      <p className="text-gray-500 text-xs mt-1">Separate multiple emails with commas</p>
                    </div>
                    
                    {channels.sms && (
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">SMS Recipients (US numbers)</label>
                        <input
                          type="text"
                          value={smsRecipients}
                          onChange={(e) => setSmsRecipients(e.target.value)}
                          placeholder="+18305551234, +12125551234"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm"
                        />
                        <p className="text-gray-500 text-xs mt-1">Format: +1XXXXXXXXXX (separate with commas)</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Channels Selection */}
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Notification Channels</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={channels.email}
                        onChange={(e) => setChannels({ ...channels, email: e.target.checked })}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <Mail size={16} className="text-blue-400" />
                      <span className="text-white text-sm">Email</span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={channels.telegram}
                        onChange={(e) => setChannels({ ...channels, telegram: e.target.checked })}
                        className="w-4 h-4 accent-blue-500"
                        disabled={!channelsStatus?.telegram?.configured}
                      />
                      <MessageCircle size={16} className="text-blue-400" />
                      <span className={`text-sm ${!channelsStatus?.telegram?.configured ? 'text-gray-500' : 'text-white'}`}>
                        Telegram {!channelsStatus?.telegram?.configured && '(Not configured)'}
                      </span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={channels.sms}
                        onChange={(e) => setChannels({ ...channels, sms: e.target.checked })}
                        className="w-4 h-4 accent-green-500"
                        disabled={!channelsStatus?.sms?.configured}
                      />
                      <Phone size={16} className="text-green-400" />
                      <span className={`text-sm ${!channelsStatus?.sms?.configured ? 'text-gray-500' : 'text-white'}`}>
                        SMS {!channelsStatus?.sms?.configured && '(Not configured)'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Preview Button */}
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>

                {/* Message Preview */}
                {showPreview && (
                  <div className={`p-4 rounded-lg ${getPriorityColor(alertType)}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {getPriorityIcon(alertType)}
                      <span className="font-bold text-white">{getAlertTypeLabel(alertType)}</span>
                    </div>
                    <p className="text-white font-semibold">{title || '[Alert Title]'}</p>
                    <p className="text-gray-300 text-sm mt-2">{message || '[Alert message will appear here]'}</p>
                    <p className="text-gray-500 text-xs mt-3">Sent from aeroTrick FBO Alert System</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendAlert}
                    disabled={sending || !title.trim() || !message.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    {sending ? 'Sending...' : 'Send Alert'}
                  </button>
                </div>
              </div>
            ) : (
              /* Alert History */
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-semibold">Recent Alerts</h4>
                  <button
                    onClick={refresh}
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    Refresh
                  </button>
                </div>
                
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {historyList.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <Bell size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No alerts sent yet</p>
                    </div>
                  ) : (
                    historyList.map((alert, idx) => (
                      <div key={alert.id || idx} className={`p-3 rounded-lg border ${getPriorityColor(alert.priority)}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {getPriorityIcon(alert.priority)}
                            <span className="text-white font-medium">{alert.title}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(alert.sent_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm mt-1 line-clamp-2">{alert.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Users size={12} className="text-gray-500" />
                            <span className="text-gray-400">{alert.recipients_count} recipients</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {alert.channels_used?.map((channel, i) => (
                              <span key={i} className="flex items-center gap-1">
                                {getChannelIcon(channel)}
                                <span className="text-gray-400">{channel}</span>
                                {i < alert.channels_used?.length - 1 && ','}
                              </span>
                            ))}
                          </div>
                          {alert.success && (
                            <CheckCircle size={12} className="text-green-400" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AlertWidget;